import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { MessageSquare, Bug, Lightbulb, HelpCircle, XCircle, AlertCircle, Clock } from 'lucide-react';

interface SupportRequest {
  id: string;
  user_id: string;
  company_id: string | null;
  request_type: string;
  subject: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'in_progress' | 'resolved' | 'closed';
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
  notes: string | null;
  user_profiles: {
    first_name: string | null;
    last_name: string | null;
    email: string;
  } | null;
  companies: {
    name: string;
  } | null;
}

const requestTypeIcons: Record<string, React.ReactNode> = {
  bug: <Bug className="w-4 h-4" />,
  feature: <Lightbulb className="w-4 h-4" />,
  support: <HelpCircle className="w-4 h-4" />,
  general: <MessageSquare className="w-4 h-4" />,
  account_deletion: <XCircle className="w-4 h-4" />,
};

const priorityColors: Record<string, string> = {
  urgent: 'bg-red-600 text-white',
  high: 'bg-orange-600 text-white',
  medium: 'bg-yellow-600 text-white',
  low: 'bg-blue-600 text-white',
};

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/50',
  in_progress: 'bg-blue-500/20 text-blue-500 border-blue-500/50',
  resolved: 'bg-green-500/20 text-green-500 border-green-500/50',
  closed: 'bg-gray-500/20 text-gray-500 border-gray-500/50',
};

function useSupportRequests() {
  return useQuery<SupportRequest[]>({
    queryKey: ['admin', 'support-requests'],
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.from as any)('support_requests')
        .select(`
          *,
          user_profiles!support_requests_user_id_fkey(first_name, last_name, email),
          companies!support_requests_company_id_fkey(name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    staleTime: 30_000, // 30 seconds
  });
}

function useUpdateSupportRequestStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase
        .from('support_requests')
        .update({ 
          status,
          updated_at: new Date().toISOString(),
          ...(status === 'resolved' || status === 'closed' ? { resolved_at: new Date().toISOString() } : {}),
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'support-requests'] });
    },
  });
}

export default function SupportRequests() {
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [selectedRequest, setSelectedRequest] = useState<SupportRequest | null>(null);

  const { data: requests = [], isLoading, error } = useSupportRequests();
  const updateStatusMutation = useUpdateSupportRequestStatus();

  const filteredRequests = requests.filter((req) => {
    if (filterStatus !== 'all' && req.status !== filterStatus) return false;
    if (filterPriority !== 'all' && req.priority !== filterPriority) return false;
    if (filterType !== 'all' && req.request_type !== filterType) return false;
    return true;
  });

  const pendingCount = requests.filter((r) => r.status === 'pending').length;
  const highPriorityCount = requests.filter((r) => r.priority === 'high' || r.priority === 'urgent').length;

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      await updateStatusMutation.mutateAsync({ id, status: newStatus });
    } catch (err) {
      console.error('Failed to update status:', err);
      alert('Failed to update status. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B1421]" />
        <span className="ml-3 text-[#A9A3A3]">Loading support requests...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-4 p-4 bg-[#9B1421]/20 border border-[#9B1421] rounded-lg">
        <p className="text-rollops-accent text-sm">
          Error loading support requests: {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[100vw] mx-auto px-4 sm:px-5 md:px-6 lg:px-7 xl:px-8 2xl:px-10 pt-2 sm:pt-3 md:pt-4 lg:pt-5 xl:pt-6 pb-8">
      <div className="mb-6">
        <h1 className="text-[#EBFFEE] text-3xl font-bold mb-2">Support Requests</h1>
        <p className="text-[#A9A3A3]">Manage and respond to user support requests</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#A9A3A3] text-sm">Total Requests</p>
              <p className="text-[#EBFFEE] text-2xl font-bold">{requests.length}</p>
            </div>
            <MessageSquare className="w-8 h-8 text-rollops-accent" />
          </div>
        </div>
        <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#A9A3A3] text-sm">Pending</p>
              <p className="text-[#EBFFEE] text-2xl font-bold">{pendingCount}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-500" />
          </div>
        </div>
        <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#A9A3A3] text-sm">High Priority</p>
              <p className="text-[#EBFFEE] text-2xl font-bold">{highPriorityCount}</p>
            </div>
            <AlertCircle className="w-8 h-8 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-[#A9A3A3] text-sm mb-2">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full bg-[#111010] border border-[#504A4A] rounded-lg px-3 py-2 text-[#EBFFEE] focus:outline-none focus:border-[#9B1421]"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div>
            <label className="block text-[#A9A3A3] text-sm mb-2">Priority</label>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="w-full bg-[#111010] border border-[#504A4A] rounded-lg px-3 py-2 text-[#EBFFEE] focus:outline-none focus:border-[#9B1421]"
            >
              <option value="all">All Priorities</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div>
            <label className="block text-[#A9A3A3] text-sm mb-2">Type</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full bg-[#111010] border border-[#504A4A] rounded-lg px-3 py-2 text-[#EBFFEE] focus:outline-none focus:border-[#9B1421]"
            >
              <option value="all">All Types</option>
              <option value="bug">Bug Report</option>
              <option value="feature">Feature Request</option>
              <option value="support">Support Request</option>
              <option value="general">General Feedback</option>
              <option value="account_deletion">Account Deletion</option>
            </select>
          </div>
        </div>
      </div>

      {/* Requests List */}
      <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg overflow-hidden">
        {filteredRequests.length === 0 ? (
          <div className="p-12 text-center">
            <MessageSquare className="w-16 h-16 text-[#504A4A] mx-auto mb-4" />
            <p className="text-[#A9A3A3] text-lg">No support requests found</p>
            <p className="text-[#706A6A] text-sm mt-2">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="divide-y divide-[#504A4A]">
            {filteredRequests.map((request) => {
              const userName = request.user_profiles
                ? `${request.user_profiles.first_name || ''} ${request.user_profiles.last_name || ''}`.trim() || request.user_profiles.email
                : 'Unknown User';
              const companyName = request.companies?.name || 'No Company';

              return (
                <div
                  key={request.id}
                  className="p-4 hover:bg-[#363232] transition-colors cursor-pointer"
                  onClick={() => setSelectedRequest(request)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-rollops-accent">
                          {requestTypeIcons[request.request_type] || <MessageSquare className="w-4 h-4" />}
                        </span>
                        <h3 className="text-[#EBFFEE] font-semibold">{request.subject}</h3>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${priorityColors[request.priority]}`}>
                          {request.priority.toUpperCase()}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs font-medium border ${statusColors[request.status]}`}>
                          {request.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      <p className="text-[#A9A3A3] text-sm mb-2 line-clamp-2">{request.description}</p>
                      <div className="flex items-center gap-4 text-xs text-[#706A6A]">
                        <span>{userName}</span>
                        <span>•</span>
                        <span>{companyName}</span>
                        <span>•</span>
                        <span>{new Date(request.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <select
                        value={request.status}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleStatusChange(request.id, e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-[#111010] border border-[#504A4A] rounded px-2 py-1 text-sm text-[#EBFFEE] focus:outline-none focus:border-[#9B1421]"
                      >
                        <option value="pending">Pending</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedRequest && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedRequest(null)}
        >
          <div
            className="bg-[#221F1F] border border-[#504A4A] rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-[#EBFFEE] text-2xl font-bold mb-2">{selectedRequest.subject}</h2>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${priorityColors[selectedRequest.priority]}`}>
                      {selectedRequest.priority.toUpperCase()}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-medium border ${statusColors[selectedRequest.status]}`}>
                      {selectedRequest.status.replace('_', ' ').toUpperCase()}
                    </span>
                    <span className="text-[#A9A3A3] text-xs">
                      {selectedRequest.request_type.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedRequest(null)}
                  className="text-[#A9A3A3] hover:text-[#EBFFEE]"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <h3 className="text-[#A9A3A3] text-sm mb-1">Description</h3>
                  <p className="text-[#EBFFEE] whitespace-pre-wrap">{selectedRequest.description}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-[#A9A3A3] text-sm mb-1">User</h3>
                    <p className="text-[#EBFFEE]">
                      {selectedRequest.user_profiles
                        ? `${selectedRequest.user_profiles.first_name || ''} ${selectedRequest.user_profiles.last_name || ''}`.trim() || selectedRequest.user_profiles.email
                        : 'Unknown'}
                    </p>
                    <p className="text-[#706A6A] text-xs">{selectedRequest.user_profiles?.email}</p>
                  </div>
                  <div>
                    <h3 className="text-[#A9A3A3] text-sm mb-1">Company</h3>
                    <p className="text-[#EBFFEE]">{selectedRequest.companies?.name || 'No Company'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-[#A9A3A3] text-sm mb-1">Created</h3>
                    <p className="text-[#EBFFEE] text-sm">
                      {new Date(selectedRequest.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-[#A9A3A3] text-sm mb-1">Request ID</h3>
                    <p className="text-[#EBFFEE] text-sm font-mono">{selectedRequest.id}</p>
                  </div>
                </div>

                {selectedRequest.notes && (
                  <div>
                    <h3 className="text-[#A9A3A3] text-sm mb-1">Notes</h3>
                    <p className="text-[#EBFFEE] whitespace-pre-wrap">{selectedRequest.notes}</p>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-4">
                <label className="text-[#A9A3A3] text-sm">Update Status:</label>
                <select
                  value={selectedRequest.status}
                  onChange={(e) => handleStatusChange(selectedRequest.id, e.target.value)}
                  className="bg-[#111010] border border-[#504A4A] rounded px-3 py-2 text-[#EBFFEE] focus:outline-none focus:border-[#9B1421]"
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
                <a
                  href={`mailto:${selectedRequest.user_profiles?.email}?subject=Re: ${selectedRequest.subject}`}
                  className="ml-auto bg-[#9B1421] hover:bg-[#7a1019] text-[#EBFFEE] px-4 py-2 rounded-lg transition-colors"
                >
                  Reply via Email
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
